import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useRef, useState } from "react";
import useSWR, { type SWRResponse } from "swr";
import { useFetching } from "@/lib/FetchingContext";
import { API_ADDRESS } from "@/lib/env";

type response<Data = any, Error = any> = SWRResponse<Data, Error | null> & { isAuthenticated: boolean }
type RequestHook = <Data = any, Error = any>(key: string, fetcher: (url: string, authToken: string) => Promise<Data>) => response<Data, Error>;

export const ErrorNotAuthenticated = new Error("User is not authenticated");

// How long to wait for Auth0 to finish loading before giving up.
// This handles the case where silent auth via iframe fails (ERR_ABORTED)
// and Auth0's isLoading never transitions to false.
const AUTH_LOADING_TIMEOUT_MS = 8000;

export const useAuthorizedRequest: RequestHook = (key, fetcher) => {
    const { getAccessTokenSilently, isAuthenticated, isLoading: authContextLoading } = useAuth0();
    const [accessToken, setAccessToken] = useState<string | null>(null);
    const [tokenLoading, setTokenLoading] = useState(true);
    const [tokenError, setTokenError] = useState<string | null>(null);
    const [authTimedOut, setAuthTimedOut] = useState(false);
    // Guard against React StrictMode double-invocation in development
    const effectCleanupRef = useRef<(() => void) | null>(null);

    // Register background fetches with the global Fetching context
    const { registerFetch } = useFetching();

    // Timeout: if Auth0 isLoading stays true longer than threshold, stop waiting
    useEffect(() => {
        if (!authContextLoading) {
            setAuthTimedOut(false);
            return;
        }

        const timer = setTimeout(() => {
            setAuthTimedOut(true);
            setTokenLoading(false);
        }, AUTH_LOADING_TIMEOUT_MS);

        return () => clearTimeout(timer);
    }, [authContextLoading]);

    useEffect(() => {
        if (!isAuthenticated) {
            setAccessToken(null);
            setTokenLoading(false);
            return
        }

        // If this effect is being re-invoked by StrictMode, bail out
        if (effectCleanupRef.current) return;

        setTokenLoading(true); // Reset loading state when re-authenticating

        let cancelled = false;
        const fetchToken = async () => {
            try {
                const token = await getAccessTokenSilently();
                if (!cancelled) {
                    setAccessToken(token);
                    setTokenError(null);
                }
            } catch (err) {
                if (!cancelled) {
                    setTokenError(err instanceof Error ? err.message : 'Unknown error');
                }
            } finally {
                if (!cancelled) {
                    setTokenLoading(false);
                }
            }
        };

        fetchToken();

        effectCleanupRef.current = () => { cancelled = true; };

        return () => {
            cancelled = true;
            effectCleanupRef.current = null;
        };
    }, [isAuthenticated]); // Only depend on isAuthenticated, not getAccessTokenSilently

    const swrResponse = useSWR(accessToken ? key : null, () => {
        if (!accessToken) {
            return Promise.reject(ErrorNotAuthenticated);
        }

        return fetcher(`${API_ADDRESS}${key}`, accessToken!)
    }, {
        refreshInterval: 1000 * 60 * 5,
        errorRetryInterval: 1000 * 60 * 5,
        revalidateOnFocus: true,
        revalidateIfStale: true,
        revalidateOnReconnect: true,
        shouldRetryOnError: false,
        dedupingInterval: 2000,
    });

    // Register/unregister fetching state with the global context
    useEffect(() => {
        if (swrResponse.isValidating) {
            return registerFetch(key);
        }
    }, [swrResponse.isValidating, key, registerFetch]);

    // If Auth0 loading timed out, treat as not authenticated
    if (authTimedOut) {
        return {
            data: null,
            error: ErrorNotAuthenticated,
            isLoading: false,
            mutate: () => Promise.resolve(),
            isValidating: false,
            isAuthenticated: false,
        } as response;
    }

    if (authContextLoading || tokenLoading) {
        return {
            data: null,
            error: null,
            isLoading: true,
            mutate: () => Promise.resolve(),
            isValidating: false,
            isAuthenticated: false,
        } as response;
    }

    if (!isAuthenticated || tokenError) {
        return {
            data: null,
            error: ErrorNotAuthenticated,
            isLoading: false,
            mutate: () => Promise.resolve(),
            isValidating: false,
            isAuthenticated: false,

        } as response;
    }

    return { ...swrResponse, isAuthenticated: true };
}